/**
 * src/shared/lib/retry.ts
 *
 * Pure utility — NO React, NO Zustand, NO store imports.
 *
 * Exports:
 *  - HttpError: typed error carrying an HTTP status code; lets withRetry
 *    discriminate between retryable (5xx) and non-retryable (4xx) failures.
 *  - withRetry<T>: wraps an async fn with bounded exponential-backoff retry.
 *    4xx → null immediately (no retry); 5xx/AbortError → retry; null on exhaustion.
 *    withRetry NEVER rejects — every failure path returns null.
 *
 * HARDEN-03: closes the gap where catalog loaders swallow transient 5xx silently
 * with no retry. The guest-first no-throw invariant is preserved at the withRetry
 * call site (useAuth), not in the loaders themselves.
 *
 * Threat mitigations:
 *  T-28-03-01 — hard cap at retries+1 total attempts (default 3) prevents retry storm.
 *  T-28-03-02 — withRetry returns null on final failure (never rejects), preserving
 *               the guest-first no-throw guarantee at the caller.
 *  T-28-03-03 — 4xx discriminated via HttpError.status → null immediately, no retry.
 */

/** Base delay in ms for exponential backoff: delay = RETRY_BACKOFF_BASE_MS × 2^attempt */
const RETRY_BACKOFF_BASE_MS = 500;

/** Default number of retry attempts (total calls = DEFAULT_RETRIES + 1) */
const DEFAULT_RETRIES = 2;

/**
 * Typed error for HTTP non-2xx responses.
 * Loaders throw this so withRetry can discriminate by status.
 */
export class HttpError extends Error {
  readonly status: number;

  constructor(status: number, message?: string) {
    super(message ?? `HTTP ${status}`);
    this.name = 'HttpError';
    this.status = status;
  }
}

/**
 * Wraps an async function with bounded exponential-backoff retry.
 *
 * - 4xx HttpError: returns null immediately, no retry (T-28-03-03).
 * - Everything else (5xx HttpError, AbortError, generic errors): retried up to
 *   `retries` times with RETRY_BACKOFF_BASE_MS × 2^i exponential backoff.
 * - Final failure (all attempts exhausted): returns null, NEVER rejects (T-28-03-02).
 * - Success: returns the resolved value.
 *
 * Max total attempts = retries + 1 (default 3).
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  retries = DEFAULT_RETRIES,
): Promise<T | null> {
  for (let i = 0; i <= retries; i++) {
    try {
      return await fn();
    } catch (err) {
      // 4xx: not retryable — return null immediately (no backoff).
      if (err instanceof HttpError && err.status >= 400 && err.status < 500) {
        return null;
      }
      // Exhausted all attempts.
      if (i === retries) {
        return null;
      }
      // 5xx or AbortError: retryable — wait with exponential backoff.
      await new Promise<void>((resolve) => setTimeout(resolve, RETRY_BACKOFF_BASE_MS * Math.pow(2, i)));
    }
  }
  // Unreachable — the loop always returns, but TypeScript needs this.
  return null;
}
